import type { QueryClient } from '@tanstack/react-query';

import type { ErrorReporter } from '@change-corgi/core/errorReporter/common';
import type { UtilityContext } from '@change-corgi/core/react/utilityContext';
import { localStorage } from '@change-corgi/core/storage';

import type { PetitionDraft } from 'src/app/shared/api/petitionDraft';
import { DraftClient, InMemoryAdapter, LocalStorageAdapter } from 'src/app/shared/api/petitionDraft';

import { getDraftUserData } from './getDraftUserData';

type UtilityErrorReporter = Pick<ErrorReporter, 'report'>;
type FindOrCreateDraftOptions = {
	errorReporter: UtilityErrorReporter;
	gqlClient: UtilityContext['gql'];
	queryClient: QueryClient;
};

const RECOMMENDED_DRAFT_TYPE = 'recommendations_draft';

function getVariant(variant: string | undefined) {
	if (variant === 'variant') return 'recommendations' as const;
	if (variant === 'variant_b') return 'recommendations_variant_b' as const;
	return 'control' as const;
}

export async function findOrCreateDraft(options: FindOrCreateDraftOptions): Promise<PetitionDraft> {
	try {
		const draftClient = new DraftClient([
			new LocalStorageAdapter(localStorage),
			new InMemoryAdapter(options.queryClient),
		]);

		// get user data
		const { experiments, viewer } = await getDraftUserData({
			fetch: options.gqlClient.fetch,
			queryClient: options.queryClient,
		});

		// find or create draft
		const draft = await draftClient.findOrCreateDraft(viewer.draftAuthorId);

		const variant = experiments.starfishSap3RecommendedAngles.variation;
		const loginState = viewer.user?.id ? 'AUTHENTICATED' : 'GUEST';

		const draftMetadata = (() => {
			return {
				user: {
					id: viewer.user?.id,
					initialLoginState: draft.metadata?.user?.initialLoginState ?? loginState,
				},
				variant: draft.metadata?.variant ?? getVariant(variant),
			};
		})();

		// reset draft if it was created with recommendations experiment but experiment is off
		const isRecommendationsVariant = !experiments.starfishSap3RecommendedAngles.variation.includes('control');
		if (draft.type === RECOMMENDED_DRAFT_TYPE && !isRecommendationsVariant) {
			return draftClient.createDraft(viewer.uuid, { metadata: draftMetadata });
		}

		// - current authenticated user id does not match the stored user id
		const didUserChange = draft.metadata?.user?.id && viewer.user?.id !== draft.metadata.user.id;
		if (didUserChange) {
			return draftClient.createDraft(viewer.uuid, { metadata: draftMetadata });
		}

		return { ...draft, metadata: { ...draft.metadata, ...draftMetadata } };
	} catch (error) {
		// report and throw
		throw new Error(`Failed to find or create draft: ${(error as Error).message}`, { cause: error });
	}
}
